import { useState } from "react";
import { toast } from "sonner";
import { FileVideo, UploadCloud, Captions, ImagePlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { grabFrame } from "@/lib/frameGrab";
import { uploadVideoInChunks, STAGE_LABEL } from "@/lib/chunkUpload";
import { SUB_LANGS } from "@/lib/subLangs";
import { EpisodeRows } from "@/components/admin/EpisodeRows";

export const EpisodeUploader = ({ list, selected, setSelected, onUploaded, refreshKey, bump }) => {
  const [number, setNumber] = useState("");
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [file, setFile] = useState(null);
  const [subtitle, setSubtitle] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [subLang, setSubLang] = useState("ur");
  const [season, setSeason] = useState(1);
  const [progress, setProgress] = useState(null);
  const [stage, setStage] = useState("uploading");
  const [error, setError] = useState("");

  const changed = () => {
    bump();
    onUploaded?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Select an anime first");
    if (!file && !videoUrl) return toast.error("Provide an MP4 file or a video URL");
    const fd = new FormData();
    fd.append("number", number);
    fd.append("season", season);
    fd.append("title", title);
    fd.append("duration", duration);
    fd.append("subtitle_lang", subLang);
    if (subtitle) fd.append("subtitle", subtitle);
    setProgress(0);
    setError("");
    try {
      let uploadWarning = null;
      const thumb = thumbnail || (file ? await grabFrame(file) : null);
      if (thumb) fd.append("thumbnail", thumb);
      if (file) {
        const up = await uploadVideoInChunks(file, (pct, st) => { setProgress(pct); setStage(st || "uploading"); });
        uploadWarning = up.warning;
        fd.append("video_url", up.url);
        fd.append("original_filename", up.filename);
        fd.append("size", up.size);
      } else {
        fd.append("video_url", videoUrl);
      }
      await api.post(`/anime/${selected}/episodes`, fd);
      toast.success(`Episode ${number} uploaded`);
      if (uploadWarning) toast.warning(uploadWarning, { duration: 15000 });
      setNumber("");
      setTitle("");
      setDuration("");
      setVideoUrl("");
      setFile(null);
      setSubtitle(null);
      setThumbnail(null);
      changed();
    } catch (err) {
      toast.error(errMsg(err));
      setError(errMsg(err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <form data-testid="admin-upload-form" onSubmit={submit} className="kp-glass p-7 flex flex-col gap-4 kp-card-clip">
      <div>
        <span className="kp-eyebrow">Step 2</span>
        <h2 className="font-display font-bold text-xl mt-1">Episode Upload</h2>
      </div>
      <select data-testid="episode-anime-select" className="kp-input" value={selected} onChange={(e) => setSelected(e.target.value)} required>
        <option value="">{list.length ? "Select anime *" : "Create an anime in the left form first"}</option>
        {list.map((a) => (
          <option key={a.id} value={a.id}>{`${a.title} (${a.episode_count} ep)`}</option>
        ))}
      </select>
      <div className="grid grid-cols-4 gap-3">
        <input data-testid="episode-season-input" className="kp-input" type="number" min="1" placeholder="Season" value={season} onChange={(e) => setSeason(Number(e.target.value) || 1)} />
        <input data-testid="episode-number-input" className="kp-input" type="number" min="1" placeholder="Ep # *" value={number} onChange={(e) => setNumber(e.target.value)} required />
        <input data-testid="episode-title-input" className="kp-input col-span-2" placeholder="Episode title" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <input data-testid="episode-duration-input" className="kp-input" placeholder="Duration (e.g. 23:40)" value={duration} onChange={(e) => setDuration(e.target.value)} />
      <label className="flex items-center gap-3 border border-dashed border-[#FF2A55]/40 hover:border-[#FF2A55] bg-[#FF2A55]/5 transition-colors px-4 py-6 cursor-pointer text-sm">
        <FileVideo size={18} className="text-[#FF2A55]" />
        <span className="truncate text-[#94A3B8]">{file ? `${file.name} · ${(file.size / 1048576).toFixed(1)} MB` : "Choose video file (MP4 / MKV / WebM / MOV)"}</span>
        <input data-testid="admin-video-file-input" type="file" accept="video/*,.mp4,.mkv,.webm,.mov,.m4v" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      <input data-testid="episode-video-url-input" className="kp-input" placeholder="...or a direct video URL (.mp4)" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} />
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <label className="flex items-center gap-3 border border-dashed border-[#00F0FF]/30 hover:border-[#00F0FF] transition-colors px-4 py-3.5 cursor-pointer text-sm">
          <Captions size={16} className="text-[#00F0FF]" />
          <span className="truncate text-[#94A3B8]">{subtitle ? subtitle.name : "Subtitles (.vtt) — optional"}</span>
          <input data-testid="episode-subtitle-file-input" type="file" accept=".vtt,text/vtt" className="hidden" onChange={(e) => setSubtitle(e.target.files?.[0] || null)} />
        </label>
        <select data-testid="episode-subtitle-lang-select" className="kp-input !w-auto" value={subLang} onChange={(e) => setSubLang(e.target.value)}>
          {SUB_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-3 border border-dashed border-white/15 hover:border-[#00F0FF]/60 transition-colors px-4 py-3.5 cursor-pointer text-sm">
        <ImagePlus size={16} className="text-[#00F0FF]" />
        <span className="truncate text-[#94A3B8]">{thumbnail ? thumbnail.name : "Episode thumbnail — optional (auto-generated from the video if empty)"}</span>
        <input data-testid="episode-thumbnail-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => setThumbnail(e.target.files?.[0] || null)} />
      </label>
      {error && (
        <div data-testid="upload-error" className="border border-[#FF2A55]/50 bg-[#FF2A55]/10 text-[#ffb3c2] text-sm px-4 py-3 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError("")} className="font-mono text-xs opacity-70 hover:opacity-100">dismiss</button>
        </div>
      )}
      {progress !== null && (
        <div>
          <div data-testid="upload-progress" className="h-1.5 bg-white/10 overflow-hidden">
            <div className={`h-full bg-gradient-to-r from-[#FF2A55] to-[#00F0FF] transition-[width] duration-200 ${stage !== "uploading" && stage !== "done" ? "animate-pulse" : ""}`} style={{ width: `${progress}%` }} />
          </div>
          {STAGE_LABEL[stage] && <p data-testid="upload-stage" className="font-mono text-[0.65rem] text-[#00F0FF] mt-1.5">{STAGE_LABEL[stage]}</p>}
        </div>
      )}
      <button data-testid="admin-submit-upload-btn" type="submit" disabled={progress !== null} className="kp-btn kp-btn-primary justify-center disabled:opacity-60">
        <UploadCloud size={14} /> {progress !== null ? (stage === "uploading" ? `Uploading ${progress}%` : "Processing…") : "Upload Episode"}
      </button>
      <EpisodeRows animeId={selected} refreshKey={refreshKey} onChanged={changed} />
    </form>
  );
};
